import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import { Card, SectionHeader } from '@/components/Card';
import { MediaUpload } from '@/components/MediaUpload';
import { facebookAdCreativesService, CreateAdCreativePayload } from '@/services/facebook-ad-creatives.service';
import { aiCampaignService } from '@/services/ai-campaign.service';
import { CreativePreview } from '@/components/facebook/CreativePreview';
import { Image as ImageIcon, Film, Link2, Type, MousePointerClick, Eye, Wand2 } from 'lucide-react';

type MediaMode = 'image' | 'video';

const CTAS: Array<{ value: NonNullable<CreateAdCreativePayload['call_to_action_type']>; label: string }> = [
  { value: 'LEARN_MORE', label: 'Learn More' },
  { value: 'SHOP_NOW', label: 'Shop Now' },
  { value: 'SIGN_UP', label: 'Sign Up' },
  { value: 'CONTACT_US', label: 'Contact Us' },
  { value: 'GET_QUOTE', label: 'Get Quote' },
  { value: 'BOOK_TRAVEL', label: 'Book Now' },
  { value: 'DOWNLOAD', label: 'Download' },
  { value: 'INSTALL_MOBILE_APP', label: 'Install App' },
  { value: 'USE_APP', label: 'Open App' },
  { value: 'WHATSAPP_MESSAGE', label: 'Send WhatsApp Message' },
  { value: 'OPEN_LINK', label: 'Open Link' },
];

export default function CreativeBuilder() {
  const nav = useNavigate();

  // Identity
  const [name, setName] = useState('');
  const [link, setLink] = useState('');

  // Media
  const [mediaMode, setMediaMode] = useState<MediaMode>('image');
  const [imageUrl, setImageUrl] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [thumbnailUrl, setThumbnailUrl] = useState('');

  // Copy
  const [headline, setHeadline] = useState('');
  const [description, setDescription] = useState('');
  const [message, setMessage] = useState('');

  // CTA
  const [cta, setCta] = useState<CreateAdCreativePayload['call_to_action_type']>('LEARN_MORE');

  const [busy, setBusy] = useState(false);
  const [enhancing, setEnhancing] = useState(false);

  const enhance = async () => {
    setEnhancing(true);
    try {
      // Enhance always uses the company default AI model (set on /settings/ai).
      // Generation features take an explicit model — but enhancement stays
      // governed centrally so the polish style is consistent across the app.
      const r = await aiCampaignService.enhanceAdCopy({
        draftName: name || undefined,
        draftHeadline: headline || undefined,
        draftDescription: description || undefined,
        draftMessage: message || undefined,
        landingPageUrl: link || undefined,
      });
      if (r.name)        setName(r.name);
      if (r.headline)    setHeadline(r.headline);
      if (r.description) setDescription(r.description);
      if (r.message)     setMessage(r.message);
      toast.success('Enhanced with AI');
    } catch {
      // Axios interceptor toasts the backend message; avoid duplicates.
    } finally { setEnhancing(false); }
  };

  const mediaProvided = mediaMode === 'image' ? !!imageUrl : !!videoUrl;
  const valid = !!name.trim() && !!link.trim() && mediaProvided;

  const save = async () => {
    setBusy(true);
    try {
      const payload: CreateAdCreativePayload = {
        name,
        link,
        headline: headline || undefined,
        description: description || undefined,
        message: message || undefined,
        call_to_action_type: cta,
      };
      if (mediaMode === 'image') {
        payload.image_url = imageUrl || undefined;
      } else {
        payload.video_url = videoUrl || undefined;
        payload.thumbnail_url = thumbnailUrl || undefined;
      }
      await facebookAdCreativesService.create(payload);
      toast.success('Creative created successfully');
      nav('/facebook/creatives');
    } catch (e: any) {
      // error handled by axios interceptor toast, but add fallback
      const msg = e?.response?.data?.message ?? e?.message ?? 'Failed to create creative';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Topbar
        title="New Ad Creative"
        breadcrumb="Facebook Ads / Creatives / New"
        right={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => nav('/facebook/creatives')}>
              Cancel
            </Button>
            <Button loading={busy} disabled={!valid} onClick={save}>
              Save creative
            </Button>
          </div>
        }
      />

      <div className="px-7 py-7 w-full">
        <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
          {/* ---- LEFT: Form ---- */}
          <div className="space-y-4">
            {/* Identity */}
            <Card>
              <SectionHeader title="Identity" icon={<Type size={16} />} />
              <FormField label="Creative name" required hint="Internal label — not shown to users">
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Summer sale image creative"
                  maxLength={255}
                />
              </FormField>
              <FormField label="Destination URL" required>
                <Input
                  type="url"
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  placeholder="https://your-site.com/landing"
                  iconLeft={<Link2 size={14} />}
                />
              </FormField>
            </Card>

            {/* Media */}
            <Card>
              <SectionHeader title="Media" icon={<ImageIcon size={16} />} subtitle="Choose image or video for this creative" />

              {/* Segmented picker */}
              <div className="mb-4 flex gap-1 rounded-xl border border-border bg-surface-alt p-1">
                <MediaModeTab
                  active={mediaMode === 'image'}
                  icon={<ImageIcon size={14} />}
                  label="Image"
                  onClick={() => setMediaMode('image')}
                />
                <MediaModeTab
                  active={mediaMode === 'video'}
                  icon={<Film size={14} />}
                  label="Video"
                  onClick={() => setMediaMode('video')}
                />
              </div>

              {mediaMode === 'image' ? (
                <FormField label="Image" required>
                  <MediaUpload
                    mode="image"
                    value={imageUrl}
                    onChange={setImageUrl}
                    folder="fb-creatives"
                  />
                </FormField>
              ) : (
                <>
                  <FormField label="Video (MP4)" required>
                    <MediaUpload
                      mode="video"
                      value={videoUrl}
                      onChange={setVideoUrl}
                      folder="fb-creatives"
                    />
                  </FormField>
                  <FormField label="Thumbnail image" hint="Optional — Meta auto-generates if omitted">
                    <MediaUpload
                      mode="image"
                      value={thumbnailUrl}
                      onChange={setThumbnailUrl}
                      folder="fb-creatives"
                    />
                  </FormField>
                </>
              )}
            </Card>

            {/* Copy */}
            <Card>
              <SectionHeader title="Copy" icon={<Type size={16} />} subtitle="Text shown in the ad" />

              {/* AI enhance bar — uses your default model from /settings/ai */}
              <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-primary/30 bg-primary-soft/40 p-2.5">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary text-white">
                  <Wand2 size={12} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-text-primary">Enhance with AI</div>
                  <div className="text-2xs text-text-muted">
                    Type rough drafts in headline / description / message, then click Enhance. Uses your default model from <a className="underline" href="/settings/ai">Settings → AI</a>.
                  </div>
                </div>
                <Button onClick={enhance} loading={enhancing}>
                  <Wand2 size={12} /> Enhance
                </Button>
              </div>

              <FormField label="Headline" hint="Bold text in the card · ≤255 chars">
                <Input
                  value={headline}
                  onChange={(e) => setHeadline(e.target.value)}
                  placeholder="25% OFF this weekend only"
                  maxLength={255}
                />
              </FormField>
              <FormField label="Description" hint="Small text below headline · ≤255 chars">
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Free shipping on all orders"
                  maxLength={255}
                />
              </FormField>
              <FormField label={`Message / body text (${message.length}/2200)`}>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={4}
                  maxLength={2200}
                  placeholder="Tell your audience why they should click — keep it punchy."
                />
              </FormField>
            </Card>

            {/* CTA */}
            <Card>
              <SectionHeader title="Call to action" icon={<MousePointerClick size={16} />} />
              <FormField label="Button label">
                <Select value={cta ?? ''} onChange={(e) => setCta(e.target.value as CreateAdCreativePayload['call_to_action_type'])}>
                  <option value="">None</option>
                  {CTAS.map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </Select>
              </FormField>
            </Card>
          </div>

          {/* ---- RIGHT: Multi-placement preview ---- */}
          <div className="lg:sticky lg:top-20 self-start space-y-3">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-text-muted">
              <Eye size={13} />
              Preview
            </div>

            <CreativePreview
              headline={headline}
              message={message}
              description={description}
              mediaUrl={mediaMode === 'image' ? imageUrl : thumbnailUrl}
              videoUrl={mediaMode === 'video' ? videoUrl : undefined}
              cta={cta ?? undefined}
              link={link}
            />
          </div>
        </div>
      </div>
    </>
  );
}

function MediaModeTab({
  active, icon, label, onClick,
}: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        'flex flex-1 items-center justify-center gap-2 rounded-lg py-2 text-sm font-semibold transition-all',
        active
          ? 'bg-surface text-text-primary shadow-soft'
          : 'text-text-muted hover:bg-surface hover:text-text-primary',
      )}
    >
      {icon}
      {label}
    </button>
  );
}
